import { Box, Card, CircularProgress, Container, IconButton, MenuItem, Stack, Table, TableBody, TableContainer, TableHead, TableRow, Typography, alpha } from "@mui/material";
import { memo, useCallback, useEffect, useState } from "react";
import { enqueueSnackbar } from "notistack";
import { useDropzone } from "react-dropzone";
import axios, { endpoints, formatEndpoint } from 'src/utils/axios';
import { useSettingsContext } from "src/components/settings";
import { Reminder } from "src/models";
import useModelData from "src/hooks/use-model-data";
import { DisplayPageData } from "src/components/display-page-data";

import PageHeader from "src/components/page-header";
import { useSort } from "src/hooks/use-sort";
import { SortableHeader } from "src/components/sortable-header";
import { TruxTableCell } from "src/components/trux-table-cell";
import { useRouter } from "src/routes/hooks";
import { useSearch } from "src/hooks/use-search";
import { SearchCard } from "src/components/search-card";
import NoneFound from "src/components/none-found";
import { formatDateForPreview } from "src/utils/date-utils";
import Iconify from "src/components/iconify/iconify";
import { ColoredIcon } from "src/components/colored-icon";
import { ExternalLink } from "src/components/external-link";
import CustomPopover from "src/components/custom-popover/custom-popover";
import { usePopover } from "src/components/custom-popover";
import SimpleDialog from "src/components/simple-dialog";
import useModelSavingData from "src/hooks/use-model-saving-data";
import TruxFormField from "src/components/inputs/trux-form-field";
import TruxTextField from "src/components/inputs/trux-text-field";
import TruxDateField from "src/components/inputs/trux-date-field";
import TruxSimpleSelectField from "src/components/inputs/trux-simple-select-field";
import { Constants } from "src/constants";

export function ReminderIcon({ intensity }: { intensity: "error" | "warning" | "success" | string }) {
  if (intensity !== "error" && intensity !== "warning" && intensity !== "success") {
    return null;
  }
  
  let icon: string;
  if (intensity === "error") {
    icon = "solar:danger-bold";
  } else if (intensity === "warning") {
    icon = "solar:danger-outline";
  } else {
    icon = "solar:danger-triangle-linear";
  }
  
  return <ColoredIcon color={intensity as "error" | "warning" | "success"} sx={{ display: "block" }}>
    <Iconify icon={icon} />
  </ColoredIcon>
}

type RemindersProps = {
  reminders?: Reminder[],
  updateData: <K extends "reminders">(field: K) => (newValue: PageModel[K]) => void
}

const Reminders = memo(({ reminders, updateData }: RemindersProps) => {
  const { sortedList, sortData } = useSort(reminders);

  const {
    searchText,
    setSearchText,
    filteredList,
  } = useSearch(sortedList, [
    "fileType",
    "originalFileName",
    "expiresAt",
    "entityType",
    "entityPreview",
    "reminderType",
    "comment",
    "fileType",
    "originalFileName",
  ]);

  const popover = usePopover();

  const [isIgnoreOpen, setIsIgnoreOpen] = useState(false);
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [savingFile, setSavingFile] = useState(false);
  const reminderToIgnore = useModelSavingData<Reminder>("", endpoints.reminders.ignoreReminder);
  const reminderToReplace = useModelSavingData<Reminder & { newFileUrl: string }>("", endpoints.reminders.replaceReminder);

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      acceptedFiles.forEach(file => {
        setSavingFile(true)

        // here we need to upload the image
        axios.postForm(
          formatEndpoint(endpoints.attachedFiles.upload, { entityId: 0, entityType: Constants.ATTACHED_FILE_ENTITY_TYPE_UNKNOWN }),
          { file }
        ).then(response => response.data).then((response) => {
          if (response.fileUrl) {
            enqueueSnackbar("Successfully uploaded the File", { variant: "success" });

            reminderToReplace.update(model => {
              model.newFileUrl = response.fileUrl;
              model.originalFileName = response.originalFileName;
            });
          }
        }).catch(() => {
          enqueueSnackbar("Your file could not be uploaded. Please try again.", { variant: "error" });
        }).finally(() => {
          setSavingFile(false);
        })
      })
    },
    [reminderToReplace]
  );


  const onRemoveFile = useCallback(() => {
    reminderToReplace.update(model => {
      model.newFileUrl = "";
    })
  }, [reminderToReplace]);


  const {
    getRootProps,
    getInputProps,
    isDragActive
  } = useDropzone({
    onDrop, accept: {
      'image/png': ['.png'],
      'image/jpg': ['.jpg'],
    }
  })

  if (!sortedList) {
    return null;
  }

  return <div>
    <SearchCard searchText={searchText} setSearchText={setSearchText} searchingFor="Reminders" />

    <Card>
      <TableContainer sx={{ position: 'relative', overflow: 'scroll' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <SortableHeader sortKey="intensity" sortData={sortData} >
                Level
              </SortableHeader>

              <SortableHeader sortKey="entityType" sortData={sortData} >
                For
              </SortableHeader>

              <SortableHeader sortKey="reminderType" sortData={sortData} >
                Type
              </SortableHeader>

              <SortableHeader sortKey="comment" sortData={sortData} >
                Comment
              </SortableHeader>

              <SortableHeader sortKey="expiresAt" sortData={sortData} >
                Expires At
              </SortableHeader>

              <SortableHeader sortKey="reminder" sortData={sortData} >
                Reminder
              </SortableHeader>

              <SortableHeader sortKey="fileType" sortData={sortData} >
                File Type
              </SortableHeader>

              <SortableHeader sortKey="originalFileName" sortData={sortData} >
                File Name
              </SortableHeader>
              <TruxTableCell />
            </TableRow>
          </TableHead>

          <TableBody>
            {filteredList.map((reminder, index) =>
            (<TableRow
              key={index}
            >
              <TruxTableCell>
                <ReminderIcon intensity={reminder.intensity} />
              </TruxTableCell>

              <TruxTableCell>
                {reminder.entityType} {reminder.entityPreview}
              </TruxTableCell>

              <TruxTableCell>
                {reminder.reminderType}
              </TruxTableCell>

              <TruxTableCell>
                {reminder.comment}
              </TruxTableCell>

              <TruxTableCell>
                {formatDateForPreview(reminder.expiresAt)}
              </TruxTableCell>
              <TruxTableCell>
                {reminder.reminder === 0 ? "Same Day" : `${reminder.reminder} Days`}
              </TruxTableCell>
              <TruxTableCell>
                {reminder.fileType}
              </TruxTableCell>
              <TruxTableCell>
                <ExternalLink href={reminder.fileUrl} rel="noreferrer" target="_blank">
                  {reminder.originalFileName}
                </ExternalLink>
              </TruxTableCell>
              <TruxTableCell>
                <IconButton onClick={(e) => {
                  reminderToReplace.set({ ...reminder, newFileUrl: "", expiresAt: undefined });
                  reminderToIgnore.set(reminder);
                  return popover.onOpen(e);
                }}>
                  <Iconify icon="solar:menu-dots-bold" rotate={1} />
                </IconButton>
              </TruxTableCell>
            </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>

    <SimpleDialog
      title="Upload Replacement"
      open={isUploadOpen}
      onCancel={() => setIsUploadOpen(false)}
      onSubmit={() => {

        if (!reminderToReplace.model) {
          return;
        }
        if (!reminderToReplace.model.newFileUrl) {
          enqueueSnackbar("Please select a File to replace", { variant: "error" });
          return;
        }
        if (!reminderToReplace.model.expiresAt) {
          enqueueSnackbar("Please select a Valid Until", { variant: "error" });
          return;
        }
        reminderToReplace.save().then(() => {
          updateData("reminders")(reminders?.filter(reminder => reminder.fileUrl !== reminderToIgnore.model?.fileUrl))
          enqueueSnackbar("Successfully Uploaded the Replacement")
          setIsUploadOpen(false)
        })
      }}
      submitDisabled={reminderToReplace.loading}
      submitButton="Upload Replacement"
      submitButtonIcon={<Iconify icon="basil:cloud-upload-outline" />}
      submitButtonColor="success">

      <Stack direction="column" gap={1}>
        <TruxFormField label="Replacement File">
          {reminderToReplace.model && <>
            {savingFile && <CircularProgress />}
            {!savingFile && reminderToReplace.model.newFileUrl && <Stack direction="row" gap={2}>
              <Box maxWidth="500px">
                <img src={reminderToReplace.model.newFileUrl} alt="Invoice Logo" />
              </Box>
              <Box>
                <IconButton color="error" onClick={onRemoveFile} >
                  <Iconify icon="solar:trash-bin-trash-bold" />
                </IconButton>
              </Box>
            </Stack>}
            {!savingFile && !reminderToReplace.model.newFileUrl && <Box>
              <Box {...getRootProps()} onFocus={null as any} onBlur={null as any}>
                <input {...getInputProps()} />

                <Box sx={{
                  m: 0.5,
                  flexShrink: 0,
                  flexGrow: 1,
                  height: "80px",
                  width: "120px",
                  borderRadius: 1,
                  cursor: 'pointer',
                  alignItems: 'center',
                  color: 'text.disabled',
                  justifyContent: 'center',
                  bgcolor: (theme) => alpha(theme.palette.grey[500], 0.08),
                  border: (theme) => `dashed 1px ${alpha(theme.palette.grey[500], 0.16)}`,
                  ...(isDragActive && {
                    opacity: 0.72,
                  }),
                  '&:hover': {
                    opacity: 0.72,
                  }
                }}><Stack spacing={0.5} paddingTop={1} alignItems="center">
                    <Iconify icon="eva:cloud-upload-fill" width={40} />
                    <Typography variant="body2">Upload File</Typography>
                  </Stack></Box>
              </Box>
            </Box>}
          </>}
        </TruxFormField>

        <TruxFormField label="Valid Until">
          <TruxDateField modelSavingData={reminderToReplace} field="expiresAt" />
        </TruxFormField>

        {reminderToReplace.model && <TruxFormField label="Reminder">
          <TruxSimpleSelectField value={reminderToReplace.model.reminder} onChange={(val) => {
            reminderToReplace.update((model) => {
              model.reminder = val;
            })
          }}>
            <MenuItem value={-1}>Off</MenuItem>
            <MenuItem value={0}>Same Day</MenuItem>
            <MenuItem value={7}>7 Days</MenuItem>
            <MenuItem value={14}>14 Days</MenuItem>
            <MenuItem value={21}>21 Days</MenuItem>
            <MenuItem value={28}>28 Days</MenuItem>
          </TruxSimpleSelectField>
        </TruxFormField>}

        <TruxFormField label="Comment">
          <TruxTextField modelSavingData={reminderToReplace} field="comment" />
        </TruxFormField>
      </Stack>
    </SimpleDialog>

    <SimpleDialog
      title="Ignore Reminder"
      open={isIgnoreOpen}
      onCancel={() => !reminderToIgnore.loading && setIsIgnoreOpen(false)}
      onSubmit={() => {
        reminderToIgnore.save().then(() => {
          updateData("reminders")(reminders?.filter(reminder => reminder.fileUrl !== reminderToIgnore.model?.fileUrl))
          enqueueSnackbar("Successfully Ignored the Reminder")
          setIsIgnoreOpen(false)
        })
      }}
      submitDisabled={reminderToIgnore.loading}
      submitButton="Ignore Reminder"
      submitButtonIcon={<Iconify icon="solar:close-circle-outline" />}
      submitButtonColor="error">

      Are you sure you would like to permanently ignore this Reminder?
    </SimpleDialog>

    <CustomPopover
      open={popover.open}
      onClose={popover.onClose}
      arrow="right-top"
    >
      <MenuItem
        onClick={() => {
          setIsUploadOpen(true);
          popover.onClose();
        }}
      >
        <Iconify icon="basil:cloud-upload-outline" />
        Upload Replacement
      </MenuItem>
      <MenuItem
        onClick={() => {
          setIsIgnoreOpen(true)
          popover.onClose();
        }}
      >
        <Iconify icon="solar:close-circle-outline" />
        Ignore
      </MenuItem>
    </CustomPopover>
  </div >;
});

type PageModel = {
  reminders?: Reminder[]
}

export default function Page() {
  const settings = useSettingsContext();
  const router = useRouter();
  const dataLoader = useCallback(() => ({
    reminders: axios.get(endpoints.reminders.findAll).then(response => response.data),
  }), []);
  const {
    data,
    updateData,
    setData,
    error,
    loading,
  } = useModelData<PageModel>(dataLoader);

  useEffect(() => {
    if (!loading && error) {
      console.log(error);
      alert(error);
    }
  }, [loading, error]);

  return (
    <Container maxWidth={settings.themeStretch ? false : 'lg'}>
      <DisplayPageData loading={loading} error={error} data={data}>
        {data && data?.reminders?.length !== 0 && <PageHeader title="Reminders" />}

        {data && data?.reminders?.length === 0 &&
          <NoneFound title={<div>
            <Box textAlign="center">
              <ColoredIcon color="success">
                <Iconify icon="solar:check-circle-bold" width={44} />
              </ColoredIcon>
            </Box>
            No Reminders Found
          </div>} message={<div>
            Reminders are a powerful tool for managing Safety and Compliance.<br />
            You can setup Reminders in the Assets portion of the Sidebar.<br />
            Reminders can be setup on any uploaded File.
          </div>} />
        }

        {data && data?.reminders?.length !== 0 && <Reminders reminders={data.reminders} updateData={updateData} />}

      </DisplayPageData>
    </Container >
  );
}
